/**
 * 통합 본인인증 서비스
 * 휴대폰 인증(Mobile-OK)과 간편인증(EZ-IOK)을 통합 관리
 *
 * @see /Users/sanggyunyou/development/PycharmProjects/ondoc-api/docs/identity-api-guide.md
 */

import MobileOKService from './MobileOKService';
import EZIOKService, { EZIOKResult, EZIOKProvider } from './EZIOKService';
import axiosClient from './axiosClient';

// API Base URL (환경변수에서 가져오거나 기본값 사용)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.ondoc.co.kr';

// 인증 방식
export type AuthMethod = 'mobile' | 'easy';

// 인증 방식 정보 (백엔드 응답 형식)
export interface AuthMethodInfo {
  mobile: {
    name: string;
    description: string;
    serviceTypes: string[];
  };
  easy: {
    name: string;
    description: string;
    providers: EZIOKProvider[];
  };
}

// 통합 인증 요청 파라미터
export interface UnifiedAuthRequestParams {
  authMethod: AuthMethod;
  resultUrl?: string; // MOKToken/keytoken 방식에서는 불필요
  // Mobile-OK 전용 파라미터
  usageCode?: string;
  serviceType?: string;
  userId?: number;
  // EZ-IOK 전용 파라미터 (통합창 방식)
  authProviderCode?: string;
  eziokServiceType?: 'auth' | 'auth_ext' | 'auth_adult' | 'sign';
  // 공통
  clientTxId?: string;
}

// 통합 인증 결과 (백엔드 응답 형식)
export interface UnifiedAuthResult {
  resultCode: string;
  resultMsg: string;
  authMethod: AuthMethod;
  txId?: string;
  // SDK 콜백에서 받은 토큰 (결과 조회 시 사용)
  token?: string;      // EZ-IOK용
  mokToken?: string;   // Mobile-OK용
  // 결과 조회 후 데이터
  ci?: string;
  di?: string;
  name?: string;
  birthDate?: string;
  gender?: string;
  nationalInfo?: string;
  mobileNo?: string;
  mobileCo?: string;
  authProvider?: string;
}

class UnifiedAuthService {
  /**
   * 사용 가능한 인증 방식 목록 조회
   * GET /func/identity/methods
   */
  async getAuthMethods(): Promise<AuthMethodInfo | null> {
    try {
      const response = await axiosClient.get<AuthMethodInfo>(
        '/func/identity/methods',
      );
      return response.data;
    } catch (err) {
      console.error('[UnifiedAuth] Failed to get auth methods:', err);
      return null;
    }
  }

  /**
   * 간편인증 사업자 목록 조회
   * GET ${API_BASE}/func/eziok/providers
   */
  async getEZIOKProviders(): Promise<EZIOKProvider[]> {
    try {
      const response = await axiosClient.get<EZIOKProvider[]>(
        `${API_BASE}/func/eziok/providers`,
      );
      return response.data || [];
    } catch (err) {
      console.error('[UnifiedAuth] Failed to get EZIOK providers:', err);
      return [];
    }
  }

  /**
   * 휴대폰 인증 시작 (Mobile-OK) - MOKToken 방식
   * MobileOKService.startAuth가 txId 생성 및 sessionStorage 저장 처리
   */
  async startMobileAuth(params: {
    usageCode: string;
    userId?: number;
    serviceType?: string;
  }): Promise<void> {
    const { usageCode, userId, serviceType = 'telcoAuth' } = params;

    console.log('[UnifiedAuth] Starting Mobile auth (MOKToken):', { usageCode, userId, serviceType });

    // MobileOKService.startAuth 사용 (MOKToken 방식)
    await MobileOKService.startAuth({
      usageCode,
      userId,
      serviceType: serviceType as 'telcoAuth' | 'telcoAuth-LMS',
    });
  }

  /**
   * 간편인증 시작 - 통합창 방식 (EZ-IOK)
   * 인증사업자를 미리 지정하여 선택 UI 스킵
   * eziok_std_process1 사용
   */
  async startEasyAuth(params: {
    authProviderCode: string;
    userId?: number;
    serviceType?: 'auth' | 'auth_ext' | 'auth_adult' | 'sign';
  }): Promise<void> {
    const { authProviderCode, userId, serviceType = 'auth' } = params;

    console.log('[UnifiedAuth] Starting Easy auth (integrated):', { authProviderCode, userId, serviceType });

    // 새 API: EZIOKService.startAuth 사용
    await EZIOKService.startAuth({
      authProviderCode,
      userId,
      serviceType,
    });
  }

  /**
   * 간편인증 시작 - 표준창 방식 (EZ-IOK)
   * SDK가 인증사업자 선택 UI 제공
   * 현재는 통합창만 지원
   */
  async startEasyAuthStandard(_params: {
    resultUrl: string;
  }): Promise<void> {
    // 표준창은 현재 미지원 - 통합창 사용 권장
    throw new Error('표준창 방식은 현재 지원하지 않습니다. 통합창(startEasyAuth)을 사용해주세요.');
  }

  /**
   * 통합 인증 시작 (authMethod로 분기)
   * MOKToken/keytoken 방식 사용
   */
  async startAuth(params: UnifiedAuthRequestParams): Promise<void> {
    const { authMethod } = params;

    if (authMethod === 'mobile') {
      await this.startMobileAuth({
        usageCode: params.usageCode || '01005',
        userId: params.userId,
        serviceType: params.serviceType,
      });
    } else if (authMethod === 'easy') {
      if (!params.authProviderCode) {
        throw new Error('간편인증은 authProviderCode가 필수입니다.');
      }
      await this.startEasyAuth({
        authProviderCode: params.authProviderCode,
        userId: params.userId,
        serviceType: params.eziokServiceType,
      });
    } else {
      throw new Error(`Unknown auth method: ${authMethod}`);
    }
  }

  /**
   * SDK 로드 (양쪽 모두)
   */
  async loadSDKs(): Promise<{ mobileOK: boolean; eziok: boolean }> {
    const results = await Promise.allSettled([
      MobileOKService.loadScript(),
      EZIOKService.loadScript(),
    ]);

    return {
      mobileOK: results[0].status === 'fulfilled',
      eziok: results[1].status === 'fulfilled',
    };
  }

  /**
   * SDK 로드 상태 확인
   */
  getSDKStatus(): { mobileOK: boolean; eziok: boolean } {
    return {
      mobileOK: MobileOKService.isLoaded(),
      eziok: EZIOKService.isLoaded(),
    };
  }

  /**
   * 브라우저 타입 감지 (Mobile-OK용)
   * WB: PC 웹, MB: 모바일 웹/앱 WebView
   */
  private detectBrowserType(): 'WB' | 'MB' {
    // Flutter 앱 WebView 체크 (localStorage + flutter_inappwebview)
    const isFlutterApp = localStorage.getItem('mobileApp') !== null &&
                         typeof (window as any).flutter_inappwebview !== 'undefined';

    if (isFlutterApp) {
      return 'MB';
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile ? 'MB' : 'WB';
  }
}

export default new UnifiedAuthService();